<template>
  <div v-if="!isExpanded" class="bill-to-edit-content collapsed">
    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, errors, inputClasses, inputId }"
        :input-id="`${id}-name`"
        :errors="v$.location.locationName.$errors"
        :label="t('common.paramName', { param: t('locations.locations') })"
        class="w-4"
      >
        <LocationQuickSearch
          :id="inputId"
          v-model="searchModel"
          v-bind="ariaProps"
          :type="LocationTypeWellKnownNaturalId.BILL_TO"
          :errors="errors"
          :class="inputClasses"
          :append-to="appendTo"
          :disabled="disabled"
          include-trimble-results
          @clear="emit('clear')"
          @select="onItemSelected"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        class="w-4"
        input-id="`${id}-bill-to-invoice-paying-party`"
        :label="t('common.invoicePayingParties')"
        :errors="v$.billToDetails.invoicePayingParty.$errors"
      >
        <BillToInvoicePayingPartyDropdown
          :id="inputId"
          v-model="internalValue.billToDetails.invoicePayingParty"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled || !!internalValue.location?.locationId"
          :append-to="appendTo"
          :placeholder="t('common.invoicePayingParties')"
        />
      </InputWrapper>
    </div>
  </div>
  <div v-else class="bill-to-edit-content">
    <ShippingPointLocationEdit
      :id="`${id}-location`"
      v-model="internalValue.location"
      :location-type="LocationTypeWellKnownNaturalId.BILL_TO"
      :append-to="appendTo"
      :disabled="disabled"
      @address-book-select="emit('address-book-select', $event)"
      @autocomplete="onAutocomplete"
      @clear="emit('clear')"
    >
      <template
        #extra-input="{
          disabled: isLocationEditDisabled,
          isUneditableFiconLocation,
        }"
      >
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          class="w-3"
          input-id="`${id}-bill-to-invoice-paying-party`"
          :label="t('common.invoicePayingParties')"
          :errors="v$.billToDetails.invoicePayingParty.$errors"
        >
          <BillToInvoicePayingPartyDropdown
            :id="inputId"
            v-model="internalValue.billToDetails.invoicePayingParty"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="isLocationEditDisabled || isUneditableFiconLocation"
            append-to="body"
            :placeholder="t('common.invoicePayingParties')"
          />
        </InputWrapper>
      </template>
    </ShippingPointLocationEdit>
  </div>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import {
  Location,
  LocationTypeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import { BillTo, EmbeddableLocation, OrderBillTo } from '@/model';

import BillToInvoicePayingPartyDropdown from '@/components/location/BillToInvoicePayingPartyDropdown.vue';
import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import ShippingPointLocationEdit from '@/components/order/ShippingPointLocationEdit.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: Object as PropType<BillTo>,
    default: () => new OrderBillTo(),
  },
  id: {
    type: String,
    required: true,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, id, appendTo, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', Location: Location): void;
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'clear'): void;
  (event: 'update:model-value', modelValue: BillTo): void;
}>();

const Clazz = Object.getPrototypeOf(modelValue.value).constructor;
const internalValue: Ref<BillTo> = ref(new Clazz(modelValue.value));

const { t } = useI18n();

type MaybeNestedValidationRule =
  | Record<string, ValidationRule>
  | Record<string, Record<string, ValidationRule>>;
const rules = computed(() => {
  const r: Record<string, MaybeNestedValidationRule> = {
    billToDetails: {
      invoicePayingParty: {
        required: required(t('common.invoicePayingParties')),
      },
    },
    location: {},
  };

  if (!isExpanded.value) {
    r.location = {
      locationName: {
        required: required(
          t('common.paramName', { param: t('locations.locations') }),
        ),
      },
    };
  }

  return r;
});
const { v$ } = useValidations(internalValue, rules);

const isAutocompleteSelection = ref(false);

const searchModel = computed(() => {
  return internalValue.value.location.toLocation();
});

const onAutocomplete = (isAutocompleteSelection: boolean) => {
  internalValue.value.location.hasBeenValidated = isAutocompleteSelection;
  emit('autocomplete', isAutocompleteSelection);
};

const isAddressBookLocation = (location: Location): boolean => {
  // Address book locations have IDs, Trimble results don't
  return !!location.id;
};

const onItemSelected = (item: Location) => {
  if (isAddressBookLocation(item)) {
    emit('address-book-select', item);
  } else {
    // Preserve location name and code if this is a Trimble result
    const newLocation = EmbeddableLocation.fromLocation(item);
    newLocation.locationName = internalValue.value.location.locationName;
    newLocation.code = internalValue.value.location.code;
    internalValue.value.location = newLocation;
  }

  isAutocompleteSelection.value = true;
};

watch(
  modelValue,
  (newVal: BillTo) => {
    const Clazz = Object.getPrototypeOf(newVal).constructor;
    internalValue.value = new Clazz(newVal);
    v$.value.$reset();
  },
  { deep: true, immediate: true },
);

watch(
  internalValue,
  (newVal: BillTo) => {
    if (!isEqual(modelValue.value, newVal)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
.bill-to-edit-content {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .row {
    display: flex;
    gap: var(--gap-1);
    padding: 0 var(--sp-6);
  }
}
</style>
